import { inject, Injectable } from '@angular/core';
import {
  CardConfig,
  IconType,
} from '../../../../../../../shared/components/generic-card/models/card-config.interface';
import { StatusCalculationService } from '../../../../../../../shared/services/status-calculation.service';
import { TpaSearchCoverageResExtended } from '../../../../../models/http/tpa-http.model';
import { TpaService } from '../../../../../services/tpa.service';
import { ActionId } from '../../../../../models/sob.enum';

@Injectable({
  providedIn: 'root',
})
export class TpaDetailViewService {
  private tpaService = inject(TpaService);
  private statusCalculationService = inject(StatusCalculationService);

  setCardConfig(data: TpaSearchCoverageResExtended[] | null): TpaSearchCoverageResExtended[] {
    if (!data) {
      return [];
    }

    data = data.map((item) => {
      const cardConfig: CardConfig = {
        header: {
          title: item.insuredFullName,
          subtitle: `MemberID: ${item.memberId}`,
        },
        sections: [
          {
            title: 'Policy information',
            fields: [
              {
                label: 'Policy Number',
                value: item.policyNumber,
              },
              {
                label: 'Legacy Policy Number',
                value: item.legacyPolicyNumber,
              },
              {
                label: 'External Policy Number',
                value: item.externalPolicyNumber,
              },
              {
                label: 'Policy Holder',
                value: item.policyHolder,
              },
              {
                label: 'Insurance Type',
                value: item.insuranceTypeName,
              },
              {
                label: 'Policy Period',
                value: `${this.tpaService.formatDate(item.policyStartDate)} - ${this.tpaService.formatDate(item.policyEndDate)}`,
              },
              {
                label: 'Insurer',
                value: item.insurer,
              },
              {
                label: 'Currency',
                value: item.rateCurrencyIso,
              },
            ],
          },
          {
            title: 'Plan Details',
            fields: [
              {
                label: 'Plan Number',
                value: item.planNumber,
              },
              {
                label: 'Plan Name',
                value: item.planName,
              },
              {
                label: 'Cover Area',
                value: item.coverArea,
              },
            ],
          },
          {
            title: 'Member Information',
            fields: [
              {
                label: 'Census Line No',
                value: item.censusLineNo,
              },
              {
                label: 'Date Of Birth',
                value: this.tpaService.formatDate(item.dateOfBirth),
              },
              {
                label: 'Period',
                value: `${this.tpaService.formatDate(item.dateJoined)} - ${this.tpaService.formatDate(item.dateLeft)}`,
              },
              {
                label: 'Insured Role',
                value: item.insuredRoleName,
              },
              {
                label: 'Member Id',
                value: item.memberId,
              },
              {
                label: 'Alternative Id',
                value: item.externalId,
              },
              {
                label: 'Telemedicine Activated',
                value: item.telemedicineEnabled ? 'Yes' : 'No',
              },
              {
                label: 'Citizenship',
                value: item.censusCitizenship,
              },
              {
                label: 'Destination / Temporary Residence - Country',
                value: item.censusDestinationCountry,
              },
            ],
          },
          {
            title: 'Contact Information',
            fields: [
              {
                label: 'Assistance Name',
                value: item.assistanceName,
              },
              {
                label: 'Hotline',
                value: item.hotline,
              },
            ],
          },
          {
            title: 'Additional Information',
            fields: [
              {
                label: 'General Underwriting Conditions',
                value: item.underwritingDetails,
              },
              {
                label: 'Individual Underwriting Conditions',
                value: item.individualUnderwritingDescription,
              },
            ],
          },
          {
            title: 'Instructions',
            fields: [
              {
                label: 'Assistance Instructions',
                value: item.assistanceInstructions,
              },
              {
                label: 'Claim Instructions',
                value: item.claimsInstructions,
              },
              {
                label: 'VIP Insured',
                value: item.tpaVipInsured ? 'Yes' : 'No',
              },
              {
                label: 'Local Provider',
                value: item.tpaLocalProvider,
              },
              {
                label: 'Additional Information',
                value: item.tpaAdditionalInformation,
              },
            ],
          },
        ],
        actions: {
          left: [
            {
              id: ActionId.openSobDialog,
              label: item.sobNotExist ? 'Benefits not exist' : 'Benefit Details',
              disabled: item.sobNotExist,
              type: 'primary',
              extraData: {
                planId: item.planId,
                showPdf: !!item.sobUploaded,
              },
            },
            {
              id: ActionId.openPolicyDialog,
              label: !item.policyHasDocument ? 'Policy Details not exist' : 'Policy Details',
              disabled: !item.policyHasDocument,
              type: 'primary',
              extraData: {
                policyId: item.policyId,
                showPdf: true,
              },
            },
          ],
        },
        options: {
          mainClass: 'tpa-group-view-cl',
          statusIcon: {
            type: this.calculateStatusIconType(item),
          },
        },
      };

      return { ...item, cardConfig };
    });

    return data;
  }

  private calculateStatusIconType(item: TpaSearchCoverageResExtended): IconType {
    if (!item) {
      return 'inactive';
    }

    // Use the computed lineStatus to determine the icon color
    return this.statusCalculationService.lineStatusToIconType(item.lineStatus);
  }
}
